import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useLocation,
  useRouteLoaderData,
} from "react-router";
import type { Route } from "./+types/root";
import "./app.css";
import { detectLocale } from "~/i18n/index";
import type { Locale } from "~/i18n/index";
import { I18nProvider } from "~/i18n/context";
import { PostHogProvider } from "~/lib/analytics";
import { ClientOnly } from "~/lib/client-only";
import { SocketProvider } from "~/lib/socket.client";
import { ConciergeChatWidget } from "~/components/concierge/ConciergeChatWidget";
import { canonicalUrl, hreflangLinks, SITE_URL, SITE_NAME, DEFAULT_OG_IMAGE } from "~/lib/seo";

export const links: Route.LinksFunction = () => [
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600&family=Cormorant+SC:wght@300;400;500;600&family=Inter:opsz,wght@14..32,300..700&display=swap",
  },
  { rel: "dns-prefetch", href: "https://images.unsplash.com" },
];

export async function loader({ request }: Route.LoaderArgs) {
  const cookieHeader = request.headers.get("Cookie");
  const acceptLanguage = request.headers.get("Accept-Language");
  const locale = detectLocale(cookieHeader, acceptLanguage);
  const url = new URL(request.url);
  return {
    locale,
    canonical: canonicalUrl(url.pathname),
    pathname: url.pathname,
    posthogKey: process.env.POSTHOG_KEY ?? "",
    posthogHost: process.env.POSTHOG_HOST ?? "https://us.i.posthog.com",
    release: process.env.RELEASE_VERSION ?? process.env.RAILWAY_GIT_COMMIT_SHA ?? "",
    environment: process.env.RAILWAY_ENVIRONMENT_NAME ?? process.env.NODE_ENV ?? "development",
  };
}

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>("root");
  const locale: Locale = data?.locale ?? "en";

  return (
    <html lang={locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
        {data?.canonical && <link rel="canonical" href={data.canonical} />}
        {data?.pathname &&
          hreflangLinks(data.pathname).map((l) => (
            <link key={l.hrefLang} rel={l.rel} hrefLang={l.hrefLang} href={l.href} />
          ))}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "Organization",
                  "@id": `${SITE_URL}/#organization`,
                  name: SITE_NAME,
                  url: SITE_URL,
                  logo: { "@type": "ImageObject", url: DEFAULT_OG_IMAGE },
                  contactPoint: {
                    "@type": "ContactPoint",
                    email: "hello@caratzon.com",
                    contactType: "customer service",
                  },
                },
                {
                  "@type": "WebSite",
                  "@id": `${SITE_URL}/#website`,
                  name: SITE_NAME,
                  url: SITE_URL,
                  publisher: { "@id": `${SITE_URL}/#organization` },
                  inLanguage: ["en", "ja", "zh-HK", "zh-CN"],
                },
              ],
            }),
          }}
        />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

const CONCIERGE_HIDDEN_PREFIXES = ["/cara", "/studio/cara"];

export default function App() {
  const { locale, posthogKey, posthogHost, release, environment } = useLoaderData<typeof loader>();
  const { pathname } = useLocation();
  const hideConcierge = CONCIERGE_HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

  return (
    <I18nProvider locale={locale}>
      <PostHogProvider posthogKey={posthogKey} posthogHost={posthogHost} release={release} environment={environment}>
        <ClientOnly fallback={<Outlet />}>
          {() => (
            <SocketProvider>
              <Outlet />
              {!hideConcierge && <ConciergeChatWidget locale={locale} />}
            </SocketProvider>
          )}
        </ClientOnly>
      </PostHogProvider>
    </I18nProvider>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (error && error instanceof Error) {
    if (import.meta.env.DEV) {
      details = error.message;
      stack = error.stack;
    }
    // Report to PostHog error tracking (client-side)
    if (typeof window !== "undefined") {
      import("posthog-js").then((m) => {
        const ph = m.default;
        if (ph.__loaded) ph.captureException(error);
      }).catch(() => {});
    }
  }

  return (
    <div
      data-theme="manufacturing"
      className="min-h-screen bg-background text-foreground flex items-center justify-center p-8"
    >
      <div className="max-w-lg space-y-4">
        <h1 className="font-display text-4xl">{message}</h1>
        <p className="text-muted-foreground">{details}</p>
        {stack && (
          <pre className="mt-4 rounded-xl border border-border p-4 text-xs overflow-x-auto text-muted-foreground">
            {stack}
          </pre>
        )}
      </div>
    </div>
  );
}
